import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions, 
  Button 
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import IdleTimeoutService from '../../services/idleTimeoutService';

/**
 * Component that manages user idle timeout by tracking activity and pinging the server
 * to update the lastActivity timestamp. The server handles the actual timeout logic,
 * but we show a warning dialog when the tab is active.
 */
const IdleTimeoutManager = () => {
  const { logout, pingServer } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ enabled: true, duration: 5 });
  const [loading, setLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  
  const lastActivityRef = useRef(Date.now());
  const warningTimeoutRef = useRef(null);
  const logoutTimeoutRef = useRef(null);
  const lastPingRef = useRef(0);
  const manualRefreshRef = useRef(false); // Flag to prevent automatic activity detection during manual refresh
  const isValidatingSession = useRef(false); // Prevent concurrent session validations
  
  // Fetch idle timeout settings from the backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const fetchedSettings = await IdleTimeoutService.getSettings();
        setSettings(fetchedSettings);
      } catch (error) {
        console.error('Error fetching idle timeout settings:', error);
        // Use default values if there's an error
        setSettings({ enabled: true, duration: 5 });
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  // Handle automatic logout due to inactivity
  const handleIdleLogout = useCallback(() => {
    setShowWarning(false);
    logout(() => {
      navigate('/login?timeout=idle');
    });
  }, [logout, navigate]);
  
  // Clear all timers helper function
  const clearAllTimers = useCallback(() => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
  }, []);
  
  // Set up warning and logout timers
  const setupTimers = useCallback(() => {
    // Clear any existing timers first
    clearAllTimers();
    
    if (settings.duration > 0 && settings.enabled) {
      const warningTime = Math.max((settings.duration * 60 - 30) * 1000, 5000); // Show warning 30 seconds before timeout
      
      console.log(`IdleTimeoutManager: Setting warning timer for ${warningTime / 1000} seconds`);
      
      // Set warning timer
      const warningId = setTimeout(() => {
        console.log('IdleTimeoutManager: Warning timer triggered');
        // Only show warning if this is still the active timer and tab is visible
        if (warningTimeoutRef.current === warningId && !document.hidden) {
          console.log('IdleTimeoutManager: Showing warning dialog');
          setShowWarning(true);
          
          // Set logout timer for 30 seconds after warning
          const logoutId = setTimeout(() => {
            console.log('IdleTimeoutManager: Logout timer triggered');
            if (logoutTimeoutRef.current === logoutId) {
              handleIdleLogout();
            }
          }, 30 * 1000);
          
          logoutTimeoutRef.current = logoutId;
        }
      }, warningTime);
      
      warningTimeoutRef.current = warningId;
    }
  }, [settings.duration, settings.enabled, clearAllTimers, handleIdleLogout]);
  
  // Function to update activity timestamp on server and reset frontend timers
  const updateActivity = useCallback(async (resetTimers = true) => {
    // Skip if we're in the middle of a manual refresh or session validation
    if (manualRefreshRef.current || isValidatingSession.current) {
      return;
    }
    
    const now = Date.now();
    lastActivityRef.current = now;
    
    // Hide warning if shown (user is active)
    setShowWarning(false);
    
    // Ping server when activity is detected to update lastActivity timestamp
    // Throttle pings to prevent rapid-fire requests (max once per 5 seconds)
    const timeSinceLastPing = now - lastPingRef.current;
    if (timeSinceLastPing >= 5000) {
      try {
        lastPingRef.current = now;
        await pingServer();
        console.log('IdleTimeoutManager: Activity pinged to server');
      } catch (error) {
        console.error('IdleTimeoutManager: Error updating activity on server:', error);
        
        // If ping fails with 401 IDLE_TIMEOUT, immediately logout
        if (error.response && error.response.status === 401 && 
            error.response.data && error.response.data.code === 'IDLE_TIMEOUT') {
          handleIdleLogout();
          return;
        }
        
        // For any network error or non-503 error, assume session is invalid and logout
        if (!error.response || error.response.status !== 503) {
          console.error('IdleTimeoutManager: Network error or session invalid, logging out');
          handleIdleLogout();
          return;
        }
        
        // Only for 503 errors (server temporarily unavailable), continue with timers
        if (error.response && error.response.status === 503) {
          console.warn('IdleTimeoutManager: Server temporarily unavailable, continuing with local timers');
        }
      }
    }
    
    // Reset timers if requested (default true)
    if (resetTimers) {
      setupTimers();
    }
  }, [pingServer, handleIdleLogout, setupTimers]);
  
  // Handle user clicking "Stay Logged In"
  const handleStayLoggedIn = async () => {
    try {
      // Set flag to prevent automatic activity detection during manual refresh
      manualRefreshRef.current = true;
      setShowWarning(false);
      
      // Clear any existing timeouts first
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
      
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
        logoutTimeoutRef.current = null;
      }
      
      // Force an immediate ping to refresh the token and update server activity
      await pingServer();
      
      // Update local activity timestamp and reset timers
      lastActivityRef.current = Date.now();
      lastPingRef.current = Date.now(); // Update ping timestamp to prevent immediate re-ping
      
    } catch (error) {
      console.error('IdleTimeoutManager: Error in handleStayLoggedIn:', error);
      
      // Check if this is a session timeout error
      if (error.response && error.response.status === 401 && 
          error.response.data && error.response.data.code === 'IDLE_TIMEOUT') {
        handleIdleLogout();
        return;
      }
      
      // If ping fails for other reasons, still hide the warning and reset timers
      setShowWarning(false);
      lastActivityRef.current = Date.now();
      
      // Set new timeouts even if ping failed
      if (settings.duration > 0) {
        const warningTime = Math.max((settings.duration * 60 - 30) * 1000, 5000);
        warningTimeoutRef.current = setTimeout(() => {
          if (!document.hidden) {
            setShowWarning(true);
            logoutTimeoutRef.current = setTimeout(() => {
              handleIdleLogout();
            }, 30 * 1000);
          }
        }, warningTime);
      }
    } finally {
      // Clear the manual refresh flag and trigger normal activity detection
      setTimeout(() => {
        manualRefreshRef.current = false;
        
        // Trigger normal activity detection to set proper timeout
        updateActivity();
      }, 2000); // 2 second delay
    }
  };
  
  useEffect(() => {
    // Don't set up activity tracking if idle timeout is disabled or still loading
    if (loading || !settings.enabled) {
      return;
    }
    
    console.log(`IdleTimeoutManager: Initializing with duration ${settings.duration} minutes`);
    
    // Initialize timers on mount
    setupTimers();
    
    // Events to track user activity (excluding mousemove for less aggressive tracking)
    const events = [
      'mousedown', 
      'keypress', 
      'keydown',
      'scroll', 
      'touchstart', 
      'click',
      'focus'
    ];
    
    // Handle user activity with throttling
    let activityThrottle = null;
    const handleUserActivity = () => {
      // Throttle activity detection to prevent excessive calls
      if (activityThrottle) {
        clearTimeout(activityThrottle);
      }
      activityThrottle = setTimeout(() => {
        console.log('IdleTimeoutManager: User activity detected');
        updateActivity(true); // Reset timers on activity
      }, 2000); // Throttle to once per 2 seconds
    };
    
    // Add event listeners with passive option for better performance
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });
    
    // Listen for visibility changes to handle tab switching
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        // Tab became visible - clear any existing warning dialog first
        setShowWarning(false);
        
        // Clear all existing timers
        clearAllTimers();
        
        // Prevent concurrent validations
        if (isValidatingSession.current) {
          return;
        }
        
        isValidatingSession.current = true;
        
        try {
          // Validate session with server
          await pingServer();
          // If ping succeeds, reset activity and timers
          updateActivity();
        } catch (error) {
          // If ping fails with IDLE_TIMEOUT, the session has expired
          if (error.response && error.response.status === 401 && 
              error.response.data && error.response.data.code === 'IDLE_TIMEOUT') {
            handleIdleLogout();
          }
          // For network errors or other non-503 errors, assume session is invalid
          else if (!error.response || error.response.status !== 503) {
            console.error('IdleTimeoutManager: Session validation failed on visibility change');
            handleIdleLogout();
          }
          // Only for 503 errors (server temporarily unavailable), reset timers locally
          else if (error.response && error.response.status === 503) {
            console.warn('IdleTimeoutManager: Server unavailable during visibility change, resetting local timers');
            updateActivity();
          }
        } finally {
          isValidatingSession.current = false;
        }
      } else {
        // Tab became hidden - immediately hide warning dialog and clear timers
        setShowWarning(false);
        clearAllTimers();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
      if (activityThrottle) {
        clearTimeout(activityThrottle);
      }
    };
  }, [settings, loading, setupTimers, updateActivity, handleIdleLogout, pingServer, clearAllTimers]);
  
  // Don't render anything if idle timeout is disabled or still loading
  if (loading || !settings.enabled) {
    return null;
  }
  
  return (
    <Dialog 
      open={showWarning} 
      onClose={() => {}} // Prevent closing by clicking outside
      disableEscapeKeyDown // Prevent closing with Escape key
      aria-labelledby="idle-timeout-dialog-title"
      aria-describedby="idle-timeout-dialog-description"
    >
      <DialogTitle id="idle-timeout-dialog-title">
        Session Timeout Warning
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="idle-timeout-dialog-description">
          Your session is about to expire due to inactivity. You will be logged out in 30 seconds.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleStayLoggedIn} color="primary" variant="contained" autoFocus>
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdleTimeoutManager;
